import { useEffect, useState } from "react";
import {
  Server,
  AlertTriangle,
  CheckCircle,
  Cpu,
  HardDrive,
  MemoryStick,
  ChevronDown,
  X as XIcon,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { SecurityScoreBadge } from "@/components/SecurityScoreBadge";
import { PageHead, StatTile, Section, FilterTabs } from "@/components/dash/primitives";
import { Button } from "@/components/ui/button";

interface ServerStats {
  total: number;
  online: number;
  offline: number;
  warning: number;
  critical: number;
}

interface StatusIssue {
  metric: string;
  value: number;
  threshold: number;
  severity: "warning" | "critical";
  icon: React.ReactNode;
  action: string;
}

function getStatusIssues(server: any): StatusIssue[] {
  const issues: StatusIssue[] = [];

  // CPU checks
  if (server.cpu_usage !== null && server.cpu_usage !== undefined) {
    if (server.cpu_usage > 90) {
      issues.push({
        metric: "CPU Usage",
        value: server.cpu_usage,
        threshold: 90,
        severity: "critical",
        icon: <Cpu className="h-3 w-3" />,
        action: "Check for runaway processes or consider scaling resources",
      });
    } else if (server.cpu_usage > 75) {
      issues.push({
        metric: "CPU Usage",
        value: server.cpu_usage,
        threshold: 75,
        severity: "warning",
        icon: <Cpu className="h-3 w-3" />,
        action: "Monitor CPU usage and identify resource-intensive processes",
      });
    }
  }

  // Memory checks
  if (server.memory_usage !== null && server.memory_usage !== undefined) {
    if (server.memory_usage > 90) {
      issues.push({
        metric: "Memory Usage",
        value: server.memory_usage,
        threshold: 90,
        severity: "critical",
        icon: <MemoryStick className="h-3 w-3" />,
        action: "Free up memory or add more RAM to the server",
      });
    } else if (server.memory_usage > 75) {
      issues.push({
        metric: "Memory Usage",
        value: server.memory_usage,
        threshold: 75,
        severity: "warning",
        icon: <MemoryStick className="h-3 w-3" />,
        action: "Review memory-heavy applications and consider optimization",
      });
    }
  }

  // Disk checks
  if (server.disk_usage !== null && server.disk_usage !== undefined) {
    if (server.disk_usage > 85) {
      issues.push({
        metric: "Disk Usage",
        value: server.disk_usage,
        threshold: 85,
        severity: "critical",
        icon: <HardDrive className="h-3 w-3" />,
        action: "Clean up disk space or expand storage capacity",
      });
    } else if (server.disk_usage > 75) {
      issues.push({
        metric: "Disk Usage",
        value: server.disk_usage,
        threshold: 75,
        severity: "warning",
        icon: <HardDrive className="h-3 w-3" />,
        action: "Review disk usage and remove unnecessary files",
      });
    }
  }

  return issues;
}

export default function DashboardPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [servers, setServers] = useState<any[]>([]);
  const [stats, setStats] = useState<ServerStats>({
    total: 0,
    online: 0,
    offline: 0,
    warning: 0,
    critical: 0,
  });
  const [loading, setLoading] = useState(true);
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"ALL" | "ONLINE" | "WARNING" | "CRITICAL">("ALL");

  useEffect(() => {
    if (profile?.tenant_id) {
      fetchServers();

      // Real-time subscription for live updates
      const channel = supabase
        .channel('dashboard-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'servers', filter: `tenant_id=eq.${profile.tenant_id}` }, () => {
          fetchServers();
        })
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [profile]);

  const fetchServers = async () => {
    try {
      const { data: serversData, error } = await supabase
        .from("v_server_status")
        .select("*")
        .eq("tenant_id", profile.tenant_id);

      if (error) throw error;

      // Fetch security-relevant metrics for accurate security scores
      const hostnames = serversData?.map((s: any) => s.hostname) || [];
      let latestMetrics: Record<string, any> = {};

      if (hostnames.length > 0) {
        const metricsPromises = hostnames.map(async (hostname: string) => {
          const { data } = await supabase
            .from('metrics_history')
            .select('hostname, secure_boot_enabled, tpm_enabled, firewall_enabled, windows_defender_enabled, windows_defender_updated, failed_login_last_24h, admin_accounts_count, bitlocker_status, smbv1_enabled, pending_updates_count, pending_security_updates, antivirus_product, antivirus_updated')
            .eq('tenant_id', profile.tenant_id)
            .eq('hostname', hostname)
            .order('timestamp', { ascending: false })
            .limit(1)
            .maybeSingle();
          return data;
        });

        const metricsResults = await Promise.all(metricsPromises);
        for (const m of metricsResults) {
          if (m?.hostname) latestMetrics[m.hostname] = m;
        }
      }

      const mergedServers = (serversData || []).map((server: any) => ({
        ...server,
        ...latestMetrics[server.hostname],
        id: server.id,
        status: server.status,
        online: server.online,
      }));

      setServers(mergedServers);

      // Calculate stats using online boolean from view
      const total = serversData?.length || 0;
      const online = serversData?.filter((s: any) => s.online).length || 0;
      const offline = total - online;
      const warning = serversData?.filter((s: any) => s.status === "warning").length || 0;
      const critical = serversData?.filter((s: any) => s.status === "critical").length || 0;

      setStats({ total, online, offline, warning, critical });
    } catch (error) {
      console.error("Error fetching servers:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusTone = (s: string) =>
    s === "online" ? "ok" : s === "critical" || s === "offline" ? "bad" : "warn";

  const StatusIconFor = ({ status }: { status: string }) => {
    if (status === "online") return <CheckCircle size={16} />;
    if (status === "warning") return <AlertTriangle size={16} />;
    if (status === "critical" || status === "offline") return <XIcon size={16} />;
    return <Server size={16} />;
  };

  const toggleExpanded = (serverId: string) => {
    setExpandedServers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serverId)) {
        newSet.delete(serverId);
      } else {
        newSet.add(serverId);
      }
      return newSet;
    });
  };

  const getEffectiveStatus = (server: any) => {
    return server.online ? "online" : ["warning", "critical"].includes(server.status) ? server.status : "offline";
  };

  if (loading) {
    return (
      <>
        <PageHead title="Dashboard" subtitle="Loading server overview..." />
        <div className="stat-grid">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card" style={{ opacity: 0.5 }}>
              <div className="stat-card__label">Loading</div>
              <div className="stat-card__value">—</div>
            </div>
          ))}
        </div>
      </>
    );
  }

  const visible =
    filter === "ALL"
      ? servers
      : servers.filter((s) => getEffectiveStatus(s) === filter.toLowerCase());

  return (
    <>
      <PageHead
        title="Dashboard"
        subtitle={`Welcome back, ${profile?.full_name || profile?.email || "user"}. Here is your server overview.`}
        right={
          <button className="btn btn--primary" onClick={() => navigate("/servers")}>
            View All Servers <ArrowRight size={14} />
          </button>
        }
      />

      <div className="stat-grid">
        <StatTile
          label="Total Servers"
          value={stats.total}
          sub="Active monitoring targets"
          icon={<Server size={16} />}
        />
        <StatTile
          label="Online"
          value={stats.online}
          sub="Online servers"
          tone="good"
          icon={<CheckCircle size={16} />}
        />
        <StatTile
          label="Warnings"
          value={stats.warning}
          sub="Require attention"
          tone="warn"
          icon={<AlertTriangle size={16} />}
        />
        <StatTile
          label="Critical"
          value={stats.critical}
          sub="Immediate action required"
          tone="bad"
          icon={<XIcon size={16} />}
        />
      </div>

      <Section
        title="Recent Servers"
        subtitle="Latest servers added to monitoring"
        right={
          <FilterTabs
            tabs={["ALL", "ONLINE", "WARNING", "CRITICAL"]}
            active={filter}
            onChange={(v) => setFilter(v as typeof filter)}
          />
        }
      >
        {visible.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: "var(--fg-3)" }}>
            <Server size={36} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
            <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
              {servers.length === 0 ? "No servers yet" : "No servers match this filter"}
            </p>
            <p style={{ fontSize: 13 }}>
              {servers.length === 0
                ? "Start by installing monitoring agents on your servers."
                : "Try a different filter."}
            </p>
          </div>
        ) : (
          visible.slice(0, 8).map((server) => {
            const effectiveStatus = getEffectiveStatus(server);
            const tone = statusTone(effectiveStatus);
            const isExpanded = expandedServers.has(server.id);
            const issues = getStatusIssues(server);
            const cpu = server.cpu_usage ?? 0;
            const mem = server.memory_usage ?? 0;
            const disk = server.disk_usage ?? 0;
            const cpuColor =
              cpu > 80 ? "var(--bad)" : cpu > 50 ? "var(--warn)" : "var(--good)";
            return (
              <div
                key={server.id}
                className={"server-row" + (isExpanded ? " expanded" : "")}
              >
                <div
                  className="server-row__main"
                  onClick={() => toggleExpanded(server.id)}
                >
                  <div className={"server-status server-status--" + tone}>
                    <StatusIconFor status={effectiveStatus} />
                  </div>
                  <div className="server-id">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <b>{server.hostname}</b>
                      <SecurityScoreBadge server={server} compact />
                    </div>
                    <div className="server-id__meta">
                      <span>{server.ip_address}</span>
                    </div>
                  </div>
                  <div className="server-metric">
                    <span className="server-metric__label">CPU</span>
                    <span
                      className="server-metric__value"
                      style={{ color: cpuColor }}
                    >
                      {cpu.toFixed(1)}%
                    </span>
                    <div className="server-metric__bar">
                      <div
                        style={{
                          width: cpu + "%",
                          background: cpuColor,
                          boxShadow: `0 0 6px ${cpuColor}`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="server-metric">
                    <span className="server-metric__label">MEM</span>
                    <span className="server-metric__value">{mem.toFixed(1)}%</span>
                    <div className="server-metric__bar">
                      <div
                        style={{ width: mem + "%", background: "var(--accent)" }}
                      />
                    </div>
                  </div>
                  <div className="server-metric">
                    <span className="server-metric__label">DISK</span>
                    <span className="server-metric__value">{disk.toFixed(1)}%</span>
                    <div className="server-metric__bar">
                      <div
                        style={{ width: disk + "%", background: "var(--accent-2)" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className={"status-chip status-chip--" + tone}>
                      <span className="status-chip__dot" />
                      {effectiveStatus}
                    </div>
                    <div
                      style={{
                        fontFamily: "Geist Mono, monospace",
                        fontSize: 11,
                        color: "var(--fg-3)",
                        marginTop: 6,
                      }}
                    >
                      {server.last_seen
                        ? new Date(server.last_seen).toLocaleString()
                        : "Never"}
                    </div>
                  </div>
                  <button className="server-row__expand" aria-label="Expand">
                    <ChevronDown size={14} />
                  </button>
                </div>
                {isExpanded && (
                  <div className="server-row__body">
                    <div>
                      <div className="issues-head">
                        {issues.length
                          ? "Issues & Recommended Actions"
                          : "Endpoint Health"}
                      </div>
                      {issues.length === 0 ? (
                        <div className="issue issue--ok">
                          <div className="issue__icon">
                            <CheckCircle size={16} />
                          </div>
                          <div>
                            <b>This endpoint is healthy</b>
                            <div className="issue__rec">
                              All metrics within normal operating thresholds.
                            </div>
                          </div>
                        </div>
                      ) : (
                        issues.map((iss, i) => (
                          <div
                            key={i}
                            className={
                              "issue issue--" +
                              (iss.severity === "critical" ? "bad" : "warn")
                            }
                          >
                            <div className="issue__icon">{iss.icon}</div>
                            <div style={{ flex: 1 }}>
                              <b>
                                {iss.metric}: {iss.value.toFixed(1)}% (threshold:{" "}
                                {iss.threshold}%)
                              </b>
                              <div className="issue__rec">{iss.action}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </Section>
    </>
  );
}
